import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Link,
  useNavigate,
  useLocation,
  useSearchParams,
} from 'react-router-dom';

import { Alert, Button, Stack } from 'react-bootstrap';

import api from 'api';

import useRequest from 'hooks/useRequest';
import useTableState from 'hooks/useTableState';
import { formatUTCDate } from 'utils/dateTime';

import Table from 'components/elements/Table';
import ListToolbar from 'components/layout/ListToolbar';
import SectionWrapper from 'components/layout/SectionWrapper';

import BurnCenterListFilter from 'components/elements/BurnCenterListFilter';

import { BurnCenterType } from 'types/burnCenter';
import useUser from 'hooks/useUser';
import { abaVerificationTypeLabels } from 'utils/enums';
import FilterEnum from './filters/data';

const formatVerificationStatus = ({
  abaVerified,
  abaVerificationType,
}: BurnCenterType): React.ReactNode => {
  if (
    abaVerified === true
    && abaVerificationType
    && abaVerificationType !== 'Unknown'
    && (abaVerificationType === 'Adult'
      || abaVerificationType === 'Pediatric'
      || abaVerificationType === 'AdultAndPediatric')
  ) {
    return abaVerificationTypeLabels[abaVerificationType] ?? abaVerificationType;
  }

  if (!abaVerificationType || abaVerificationType === 'Unknown') {
    return (abaVerified === true) ? 'Yes' : 'No';
  }

  const status = abaVerified === true ? 'Yes' : 'No';
  const type = abaVerificationTypeLabels[abaVerificationType] ?? abaVerificationType;
  return `${status} (${type})`;
};

type FilterEnumType = typeof FilterEnum;
type FilterKey = FilterEnumType[keyof FilterEnumType];

type FilterType = {
  [_key in FilterKey]: number | string | null;
};

type ListDataResponse = {
  data: BurnCenterType[];
  count: number;
  page: number;
  pageSize: number;
  pageCount: number;
};

const columns = [
  {
    id: 'id',
    label: 'Id',
    nonSortable: true,
    headerStyle: { width: '7%' },
  },
  {
    id: 'name',
    label: 'Burn Center',
    headerStyle: { width: '25%' },
  },
  {
    id: 'hospitalName',
    label: 'Hospital',
  },
  {
    id: 'burnCenterAccessLevel',
    label: 'Access Level',
    headerStyle: { width: '15%' },
  },
  {
    id: 'abaVerificationType',
    label: 'Verified',
    headerStyle: { width: '18%' },
    override: formatVerificationStatus,
    nonSortable: true,
  },
  {
    id: 'createdAt',
    label: 'Created',
    override: ({ createdAt }: BurnCenterType) => formatUTCDate(createdAt),
  },
  {
    id: 'users',
    label: 'User count',
    headerStyle: { width: '10%' },
  },
];

const BurnCentersList: React.FC = () => {
  const navigate = useNavigate();
  const { isAbaAdmin, isBcAdmin } = useUser();

  const { pathname } = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';

  const initFilters: FilterType = {
    [FilterEnum.search]: searchQuery || null,
  };

  const {
    isLoading,
    error,
    doRequest: getListData,
    data: listData,
  } = useRequest<ListDataResponse>(api.getBurnCenters);

  const {
    page,
    handlePageChange,
    sortBy,
    sortOrder,
    handleSortChange,
    filters,
    handleFilterChange,
  } = useTableState<FilterType>('name', 'asc', initFilters);

  const requestParams = useMemo(
    () => ({
      sortBy,
      sortOrder,
      ...filters,
    }),
    [sortBy, sortOrder, filters],
  );

  const fetchListData = useCallback(() => {
    getListData({
      ...requestParams,
      Page: page,
    });
  }, [getListData, page, requestParams]);

  useEffect(() => {
    fetchListData();
  }, [fetchListData]);

  return (
    <SectionWrapper title="Burn Centers">
      <Stack gap={3} style={{ minWidth: 0 }}>
        <ListToolbar
          left={
            !isAbaAdmin && !isBcAdmin ? (
              <Button as={Link as any} to="new" variant="secondary">
                New Burn Center
              </Button>
            ) : null
          }
          right={(
            <div style={{ width: '100%', maxWidth: '400px', minWidth: 0 }}>
              <BurnCenterListFilter
                initialValue={filters[FilterEnum.search] as string}
                onChange={({ search }) => {
                  setSearchParams((prev) => {
                    const p = new URLSearchParams(prev);
                    if (search) p.set('search', search);
                    else p.delete('search');
                    return p;
                  });

                  handleFilterChange(FilterEnum.search, search);
                }}
              />
            </div>
          )}
        />
        {error ? (
          <Alert variant="danger">Error Loading List</Alert>
        ) : (
          <Table<BurnCenterType>
            columns={columns}
            items={listData?.data || []}
            minTableWidth="900px"
            handlePageChange={handlePageChange}
            handleOrderChange={handleSortChange}
            order={sortBy}
            orderDirection={sortOrder}
            pagination={{
              total: listData?.count || 0,
              limit: listData?.pageSize || 10,
              page,
            }}
            onClickRow={({ id }: BurnCenterType) => navigate(`${pathname}/${id}`)}
            isLoading={isLoading}
          />
        )}
      </Stack>
    </SectionWrapper>
  );
};

export default BurnCentersList;
